import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { EditIcon, ViewIcon } from '@/assets/icons';
import { ActionsDropdown } from '@/components/actions-dropdown';
import { DownloadDocuments } from '@/components/download';
import PropagationWrapper from '@/components/propagation-wrapper/PropagationWrapper';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { useNotify } from '@/hooks';
import { API } from '@/services/api.service';
import { QUERY_KEY } from '@/shared/constants';

const AcceptedButtonPopConfirm = lazy(
  () => import('@/components/request/actions/AcceptedButtonPopConfirm')
);

const RejectModalForm = lazy(
  () => import('@/components/request/form/RejectModalForm')
);

const RequestDeleteModal = lazy(
  () => import('@/components/request/actions/RequestDeleteModal')
);

export default function HRDocumentsActions({ record }) {
  const { t } = useTranslation('menus');

  const { setQueryParams } = useQueryParamsContext();
  const notification = useNotify();

  const requestId = record?.request?._id;
  const documentId = record?._id;
  const typeOfLeave = record?.typeOfLeave?.key;
  const companyId = record?.company?._id;

  const isFile = !!record.file;
  const isContract = record.type === 'CONTRACT';
  const statusKey = record?.status?.key;
  const isAccepted = statusKey === 'ACCEPTED';
  const isRejected = statusKey === 'REJECTED';

  // Open the rendered document as a PDF inline in a new tab (preview). Available
  // for any downloadable, non-file document (uploaded files already preview via
  // their own download button).
  const { mutate: viewPdf, isPending: isViewing } = useMutation({
    mutationFn: () => API.documents.viewDocument(documentId),
    onError: (error) => {
      notification.error({ message: error?.message || 'Error' });
    },
  });

  const handleAction = (action) => {
    setQueryParams({
      ['route']: 'create-new-request',
      ['typeOfLeaveSelect']: typeOfLeave,
      ['companyId']: companyId,
      ['requestId']: requestId,
      ['action']: action,
    });
  };

  const {
    actionButtons,
    approveButton,
    deleteButton,
    downloadButton,
    editButton,
    rejectButton,
    viewButton,
  } = record;

  return (
    <PropagationWrapper className="flex w-full justify-end">
      <div className="flex gap-2">
        {downloadButton && (
          <DownloadDocuments
            className="h-8 w-8"
            documentId={documentId}
            isFile={isFile}
            record={record}
          />
        )}

        {!isAccepted && approveButton && (
          <Suspense
            fallback={<div className="h-8 w-8 animate-pulse bg-zinc-100" />}
          >
            <AcceptedButtonPopConfirm
              id={requestId}
              queryKey={QUERY_KEY.employees}
            />
          </Suspense>
        )}

        {!isRejected && rejectButton && (
          <Suspense
            fallback={<div className="h-8 w-8 animate-pulse bg-zinc-100" />}
          >
            <RejectModalForm id={requestId} queryKey={QUERY_KEY.employees} />
          </Suspense>
        )}

        {(actionButtons || (downloadButton && !isFile)) && (
          <ActionsDropdown metadata={{ overlayStyle: { zIndex: 1 } }}>
            {() => (
              <>
                {downloadButton && !isFile && (
                  <Button
                    type="button"
                    className="btn-default w-full justify-start px-2"
                    loading={isViewing}
                    onClick={() => viewPdf()}
                    icon={<ViewIcon className="w-[15px]" />}
                  >
                    {t('viewPdf', { defaultValue: 'Pregled (PDF)' })}
                  </Button>
                )}

                {!isAccepted && !isFile && editButton && (
                  <Button
                    type="button"
                    className="btn-default h-7 w-7 justify-start px-2"
                    onClick={() => handleAction('edit')}
                    icon={<EditIcon className="w-[14px]" />}
                  >
                    {t('edit')}
                  </Button>
                )}

                {!isFile && !isContract && viewButton && (
                  <Button
                    type="button"
                    className="btn-default w-full justify-start px-2"
                    onClick={() => handleAction('view')}
                    icon={<ViewIcon className="w-[15px]" />}
                  >
                    {t('view')}
                  </Button>
                )}

                {deleteButton && (
                  <Suspense
                    fallback={
                      <div className="h-9 w-full animate-pulse bg-zinc-100" />
                    }
                  >
                    <div className="border-t">
                      <RequestDeleteModal
                        record={record}
                        isFile={isFile}
                        isContract={isContract}
                        queryKey={QUERY_KEY.employeeHrDocuments}
                      />
                    </div>
                  </Suspense>
                )}
              </>
            )}
          </ActionsDropdown>
        )}
      </div>
    </PropagationWrapper>
  );
}
